// secondary.js is an ES module — loaded via <script type="module">.
// Imports `Component` from @theme/component (Horizon's Component base class)
// and the shared focus/media/timing helpers from @theme/main.
import { Component } from "@theme/component";
import { trapFocus, removeTrapFocus, debounce, pauseAllMedia, isClickedOutside } from "@theme/main";

window.Shopify = window.Shopify || {};
window.Shopify.isMe2 = true;

try {
	document.querySelector(":focus-visible");
} catch (t) {
	focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
	let t = ["ARROWUP", "ARROWDOWN", "ARROWLEFT", "ARROWRIGHT", "TAB", "ENTER", "SPACE", "ESCAPE", "HOME", "END", "PAGEUP", "PAGEDOWN"],
		e = null,
		i = null;
	window.addEventListener("keydown", (e) => {
		t.includes(e.code.toUpperCase()) && (i = !1);
	}),
		window.addEventListener("mousedown", (t) => {
			i = !0;
		}),
		window.addEventListener(
			"focus",
			() => {
				e && e.classList.remove("focused"), i || (e = document.activeElement).classList.add("focused");
			},
			!0
		);
}

class ProductRecommendations extends Component {
	#observer = null;

	constructor() {
		super();
	}
	connectedCallback() {
		super.connectedCallback();
		// Skip the lazy fetch if Shopify already populated this element with
		// content via the Section Rendering API (theme editor section reload,
		// quick-add modal re-mount, etc.). data-recommendations-performed is
		// the source of truth: liquid emits "true" only when the recommendation
		// computation has actually run.
		//
		// On the FIRST page load, Shopify's `recommendations` object is lazy —
		// it doesn't compute on initial render. So performed="false" here and
		// the IntersectionObserver+fetch path below runs as before. Space is
		// reserved via content-visibility/contain-intrinsic-size in liquid.
		if (this.dataset.recommendationsPerformed === "true" && this.children.length > 0) return;
		let t = (t, e) => {
			t[0].isIntersecting &&
				(e.unobserve(this),
				fetch(this.dataset.url)
					.then((t) => t.text())
					.then((t) => {
						let e = document.createElement("div");
						e.innerHTML = t;
						let i = e.querySelector("product-recommendations");
						let hasContent = i && i.innerHTML.trim().length;
						if (hasContent) {
							// Set content and clear min-height in the same JS
							// frame so the layout shift is a single event (no
							// flash of "reserved space → empty → content").
							// The reserved min-height absorbs most of the
							// content height; any residual difference between
							// estimate and actual is one small shift here.
							this.innerHTML = i.innerHTML;
							this.style.minHeight = "";
						}
						if (!this.querySelector("slideshow-component") && this.classList.contains("complementary-products")) {
							// Complementary returned empty — collapse and remove.
							// Set ALL space-reservation styles to 0 BEFORE
							// remove() so the layout shift is one event (the
							// removal itself) not two stacked frames.
							this.style.margin = "0";
							this.style.padding = "0";
							this.style.minHeight = "0";
							this.remove();
						} else if (!hasContent) {
							// Related-products (or any non-complementary instance)
							// returned empty — hide so the reserved min-height
							// space doesn't show as an empty box.
							this.style.display = "none";
						}
						if (e.querySelector(".grid__item")) this.classList.add("product-recommendations--loaded");
					})
					.catch((t) => {
						console.error(t);
					}));
		};
		this.#observer = new IntersectionObserver(t.bind(this), {
			rootMargin: "0px 0px 400px 0px",
		});
		this.#observer.observe(this);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		// Fallback for the case the element disconnects before ever intersecting.
		this.#observer?.disconnect();
	}
}
customElements.define("product-recommendations", ProductRecommendations);

class MenuDrawer extends Component {
	constructor() {
		super(), (this.mainDetailsToggle = this.querySelector("details")), this.addEventListener("keyup", this.onKeyUp.bind(this)), this.addEventListener("focusout", this.onFocusOut.bind(this)), this.bindEvents();
	}
	bindEvents() {
		this.querySelectorAll("summary").forEach((t) => t.addEventListener("click", this.onSummaryClick.bind(this))), this.querySelectorAll("button:not(.menu-drawer__close-menu-btn)").forEach((t) => t.addEventListener("click", this.onCloseButtonClick.bind(this)));
	}
	onKeyUp(t) {
		if ("ESCAPE" !== t.code.toUpperCase()) return;
		let e = t.target.closest("details[open]");
		e && (e === this.mainDetailsToggle ? this.closeMenuDrawer(t, this.mainDetailsToggle.querySelector("summary")) : this.closeSubmenu(e));
	}
	onSummaryClick(t) {
		let e = t.currentTarget,
			i = e.parentNode,
			s = i.closest(".has-submenu"),
			n = i.hasAttribute("open"),
			o = window.matchMedia("(prefers-reduced-motion: reduce)");

		function r() {
			trapFocus(e.nextElementSibling, i.querySelector("button")), e.nextElementSibling.removeEventListener("transitionend", r);
		}
		i === this.mainDetailsToggle
			? (n && t.preventDefault(), n ? this.closeMenuDrawer(t, e) : this.openMenuDrawer(e), window.matchMedia("(max-width: 990px)") && document.documentElement.style.setProperty("--viewport-height", `${window.innerHeight}px`))
			: setTimeout(() => {
					i.classList.add("menu-opening"), e.setAttribute("aria-expanded", !0), s && s.classList.add("submenu-open"), !o || o.matches ? r() : e.nextElementSibling.addEventListener("transitionend", r);
			  }, 100);
	}
	openMenuDrawer(t) {
		setTimeout(() => {
			this.mainDetailsToggle.classList.add("menu-opening");
		}),
			t.setAttribute("aria-expanded", !0),
			trapFocus(this.mainDetailsToggle, t),
			document.body.classList.add(`overflow-hidden-sticky`);
	}
	closeMenuDrawer(t, e = !1) {
		void 0 !== t &&
			(this.mainDetailsToggle.classList.remove("menu-opening"),
			this.mainDetailsToggle.querySelectorAll("details").forEach((t) => {
				t.removeAttribute("open"), t.classList.remove("menu-opening");
			}),
			this.mainDetailsToggle.querySelectorAll(".submenu-open").forEach((t) => {
				t.classList.remove("submenu-open");
			}),
			document.body.classList.remove(`overflow-hidden-sticky`),
			removeTrapFocus(e),
			this.closeAnimation(this.mainDetailsToggle));
	}
	onFocusOut(t) {
		setTimeout(() => {
			this.mainDetailsToggle.hasAttribute("open") && !this.mainDetailsToggle.contains(document.activeElement) && this.closeMenuDrawer();
		});
	}
	onCloseButtonClick(t) {
		let e = t.currentTarget.closest("details");
		this.closeSubmenu(e);
	}
	closeSubmenu(t) {
		let e = t.closest(".submenu-open");
		e && e.classList.remove("submenu-open"), t.classList.remove("menu-opening"), t.querySelector("summary").setAttribute("aria-expanded", !1), removeTrapFocus(t.querySelector("summary")), this.closeAnimation(t);
	}
	closeAnimation(t) {
		let e,
			i = (s) => {
				void 0 === e && (e = s);
				let n = s - e;
				n < 400 ? window.requestAnimationFrame(i) : (t.removeAttribute("open"), t.closest("details[open]") && trapFocus(t.closest("details[open]"), t.querySelector("summary")));
			};
		window.requestAnimationFrame(i);
	}
}
customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
	constructor() {
		super(), this.querySelectorAll(".menu-drawer__close-menu-btn").forEach((t) => t.addEventListener("click", this.closeButtonClick.bind(this)));
	}
	openMenuDrawer(t) {
		(this.header = this.header || document.querySelector(".section-header")),
			(this.borderOffset = this.borderOffset || this.closest(".header-wrapper").classList.contains("header-wrapper--border-bottom") ? 1 : 0),
			document.documentElement.style.setProperty("--header-bottom-position", `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`),
			this.header.classList.add("menu-open"),
			setTimeout(() => {
				this.mainDetailsToggle.classList.add("menu-opening");
			}),
			t.setAttribute("aria-expanded", !0),
			trapFocus(this.mainDetailsToggle, t),
			document.body.classList.add(`overflow-hidden-sticky`);
	}
	closeMenuDrawer(t, e) {
		super.closeMenuDrawer(t, e), this.header.classList.remove("menu-open");
	}
	closeButtonClick(t) {
		this.closeMenuDrawer(t, this.mainDetailsToggle.querySelector("summary")), this.querySelector(".header__icon--menu[aria-expanded=true]").setAttribute("aria-expanded", "false");
	}
}
customElements.define("header-drawer", HeaderDrawer);

class ProductsMegaMenu extends Component {
	constructor() {
		super(),
			(this.details = this.querySelector('details[id^="ProductsMegaMenu-"]')),
			(this.mainLink = this.querySelector("summary.header__menu-item")),
			(this.body = this.querySelector(".products-mega-menu__body")),
			(this.overlay = this.querySelector(".products-mega-menu__overlay")),
			(this.items = this.querySelectorAll('[id^="ProductsMegaMenu-Item"]')),
			(this.links = this.querySelectorAll('[id^="ProductsMegaMenu-Link"]')),
			// AbortController owns every listener registered below so they all
			// detach in one call from disconnectedCallback. Without this, the
			// document-level click handler would leak on customizer reloads.
			(this.abortController = new AbortController());
		const signal = this.abortController.signal;
		this.mainLink.addEventListener("mouseover", this.openMenu.bind(this), { signal });
		this.overlay.addEventListener("mouseover", this.closeMenu.bind(this), { signal });
		this.overlay.addEventListener("click", this.closeMenu.bind(this), { signal });
		document.addEventListener("click", this.outsideClick.bind(this), { signal });
		this.links.forEach((t) => {
			t.addEventListener("mouseover", this.displayContent.bind(this), { signal });
		});
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.abortController?.abort();
	}
	displayContent(t) {
		this.items.forEach((t) => {
			t.classList.remove("products-mega-menu__item--active");
		}),
			t.target.closest('[id^="ProductsMegaMenu-Item"]').classList.add("products-mega-menu__item--active");
	}
	openMenu() {
		document.querySelectorAll('[id^="Details-HeaderMenu"], [id^="ProductsMegaMenu-"]').forEach((t) => {
			t.removeAttribute("open");
		}),
			this.details.setAttribute("open", "");
	}
	closeMenu() {
		this.details.removeAttribute("open");
	}
	outsideClick(t) {
		if (this.details.hasAttribute("open") && isClickedOutside(t, this.body)) this.closeMenu();
	}
}
customElements.define("products-mega-menu", ProductsMegaMenu);

class ModalDialog extends Component {
	constructor() {
		super(),
			this.querySelector('[id^="ModalClose-"]').addEventListener("click", this.hide.bind(this, !1)),
			this.addEventListener("keyup", (t) => {
				"ESCAPE" === t.code.toUpperCase() && this.hide();
			}),
			this.classList.contains("media-modal")
				? this.addEventListener("pointerup", (t) => {
						"mouse" !== t.pointerType || t.target.closest("deferred-media, product-model") || this.hide();
				  })
				: this.addEventListener("click", (t) => {
						t.target === this && this.hide();
				  });
	}
	connectedCallback() {
		super.connectedCallback();
		this.moved || ((this.moved = !0), document.body.appendChild(this));
	}
	show(t) {
		this.openedBy = t;
		let e = this.querySelector(".template-popup");
		document.body.classList.add("overflow-hidden-sticky"), this.setAttribute("open", ""), e && e.loadContent(), trapFocus(this, this.querySelector('[role="dialog"]')), pauseAllMedia();
	}
	hide() {
		document.body.classList.remove("overflow-hidden-sticky"), document.body.dispatchEvent(new CustomEvent("modalClosed")), this.removeAttribute("open"), removeTrapFocus(this.openedBy), pauseAllMedia();
	}
}
customElements.define("modal-dialog", ModalDialog);

class ModalOpener extends Component {
	constructor() {
		super();
		let t = this.querySelector("button:not(.internal-video__play, .internal-video__sound-btn)");
		if (!t) return;
		t.addEventListener("click", () => {
			let e = document.querySelector(this.getAttribute("data-modal"));
			e && e.show(t);
		});
	}
}
customElements.define("modal-opener", ModalOpener);

class ModalV2 extends Component {
	constructor() {
		super();

		this.modalId = this.dataset.modalId;

		// If the modal is already in the modals manager, or another modal with the same ID exists, do not append it again
		const modalsManager = document.querySelector(".modals-manager");
		if (this.parentNode === modalsManager || Array.from(modalsManager.children).some((child) => child.dataset.modalId === this.modalId)) return;

		// Append the modal to the modals manager container
		modalsManager.appendChild(this);

		this.openBtns = this.querySelectorAll(".modal-v2__open-btn");
		this.closeBtns = this.querySelectorAll(".modal-v2__close-btn");
		this.openBtns.forEach((t) => {
			t.addEventListener("click", this.openModal.bind(this));
		});
		this.closeBtns.forEach((t) => {
			t.addEventListener("click", this.closeModal.bind(this));
		});
	}
	openModal() {
		this.dataset.open = "true";
		document.body.classList.add("overflow-hidden-sticky");
	}
	closeModal() {
		this.dataset.open = "false";
		document.body.classList.remove("overflow-hidden-sticky");
	}
}
customElements.define("modal-v2", ModalV2);

class TouchDragListener {
	constructor({ el, touchStartCallback, touchEndCallback, touchMoveCallback, showLog }) {
		this.el = el;
		this.touchStartCallback = touchStartCallback;
		this.touchEndCallback = touchEndCallback;
		this.touchMoveCallback = touchMoveCallback;
		this.showLog = showLog;

		this.active = false;
		this.currentY;
		this.initialY;
		this.yOffset = 0;

		this.dragStart = this.dragStart.bind(this);
		this.dragEnd = this.dragEnd.bind(this);
		this.drag = this.drag.bind(this);

		this.el.addEventListener("mousedown", this.dragStart);
		this.el.addEventListener("mouseleave", this.dragEnd);
		this.el.addEventListener("mouseup", this.dragEnd);
		this.el.addEventListener("mousemove", this.drag);

		this.el.addEventListener("touchstart", this.dragStart);
		this.el.addEventListener("touchend", this.dragEnd);
		this.el.addEventListener("touchmove", this.drag);
	}

	dragStart(e) {
		this.active = true;
		this.el.classList.add("active");

		if (e.type === "touchstart") {
			this.initialY = e.touches[0].clientY - this.yOffset;
		} else {
			this.initialY = e.clientY - this.yOffset;
		}

		if (!this.touchStartCallback) return;

		this.touchStartCallback({
			el: this.el,
			active: this.active,
			currentY: this.currentY,
			initialY: this.initialY,
			yOffset: this.yOffset,
		});
	}

	dragEnd(e) {
		this.active = false;
		this.el.classList.remove("active");

		this.yOffset = 0;

		this.initialY = this.currentY;

		if (!this.touchEndCallback) return;

		this.touchEndCallback({
			el: this.el,
			active: this.active,
			currentY: this.currentY,
			initialY: this.initialY,
			yOffset: this.yOffset,
		});
	}

	drag(e) {
		if (!this.active) return;
		e.preventDefault();

		if (e.type === "touchmove") {
			this.currentY = e.touches[0].clientY - this.initialY;
		} else {
			this.currentY = e.clientY - this.initialY;
		}

		this.yOffset = this.currentY;

		if (!this.touchMoveCallback) return;

		this.touchMoveCallback({
			el: this.el,
			active: this.active,
			currentY: this.currentY,
			initialY: this.initialY,
			yOffset: this.yOffset,
		});

		if (this.showLog) {
			console.log({
				active: this.active,
				initialY: this.initialY,
				currentY: this.currentY,
				yOffset: this.yOffset,
			});
		}
	}
}
class VariantPickerModal extends Component {
	constructor() {
		super();

		const modalsManager = document.querySelector(".modals-manager");
		if (this.parentNode === modalsManager) return;
		modalsManager.appendChild(this);

		this.alert = this.querySelector(".alert");
		this.alertParagraph = this.querySelector(".alert__paragraph");
		this.templateText = this.alertParagraph.dataset.templateText;
		this.scrim = this.querySelector(".bottom-sheet__scrim");
		this.handle = this.querySelector(".bottom-sheet__handlebar-button");
		this.sheet = this.querySelector(".bottom-sheet__sheet");
		this.activate = this.activate.bind(this);
		this.deactivate = this.deactivate.bind(this);
		this.initialized = false;

		this.scrim.addEventListener("click", this.deactivate);
		this.handle.addEventListener("click", this.deactivate);

		this.sheetListener = new TouchDragListener({
			el: this.sheet,
			touchStartCallback: ({ el, active, initialY, currentY, yOffset }) => {
				el.style.setProperty("--translateY", `translateY(0)`);
				el.style.setProperty("transition", `unset`);
			},
			touchEndCallback: ({ el, active, initialY, currentY, yOffset }) => {
				el.style.setProperty("--translateY", `translateY(${currentY}px)`);
			},
			touchMoveCallback: ({ el, active, initialY, currentY, yOffset }) => {
				if (currentY < 0) {
					currentY -= currentY;
					// Close the modal if dragged up beyond 50% of its height
				} else if (currentY >= this.sheet.offsetHeight * 0.6) {
					this.deactivate(currentY);
					return;
				}

				el.style.setProperty("--translateY", `translateY(${currentY}px)`);
			},
		});

		this.scrimListener = new TouchDragListener({
			el: this.scrim,
			touchMoveCallback: ({ el, active, initialY, currentY, yOffset }) => {
				if (currentY >= 83) {
					this.deactivate();
					return;
				}
			},
		});

		this.init();
	}
	init() {
		if (this.initialized) return;

		const variantBtns = document.querySelectorAll(".ui--list-item");
		for (let i = 0; i < variantBtns.length; i++) {
			variantBtns[i].addEventListener("click", function (ev) {
				const variantPickerModal = document.querySelector("variant-picker-modal");
				variantPickerModal.deactivate();

				const variantId = document.querySelector(".product-variant-id");
				variantId.value = ev.target.getAttribute("data-variant-id");
				const mainProductForm = document.querySelector(".main-product-form");
				mainProductForm.setAttribute("data-from-size-modal", true);
				document.querySelector(".main-product-atc").click();
			});
		}

		const regex = /(\d+)\s*cm[\s\S]{0,120}?\b(?:porte|porta|lleva|wears|trage|trägt|traje|wears|is\s+wearing)\b[\s\S]{0,30}?\b(?:taille|size|talla|größe|grösse|chaussure|shoe|EU|US|UK)?\s*\b(XXXL|XXL|XL|L|M|S|XS|[0-9]{2}(?:[.,]\d+)?(?:\s*(?:EU|US|UK))?)\b/i;
		const productDescription = document.querySelector(".accordion__content"); // description should always be first
		if (!productDescription) return;
		for (const child of productDescription.children) {
			if (child.tagName.toLowerCase() === "p") {
				const match = child.textContent.match(regex);
				if (match) {
					const [, height, size] = match;
					this.templateText = this.templateText.replace("[height]", height);
					this.templateText = this.templateText.replace("[size]", size);
					this.alertParagraph.textContent = this.templateText;
					this.alert.style.display = "flex";
					break;
				}
			}
		}

		this.initialized = true;
	}
	activate(e) {
		if (e) e.preventDefault();
		document.body.classList.add("overflow-hidden-sticky");
		this.classList.add("active");
		this.sheet.style.removeProperty("transition");
	}
	deactivate(translateY) {
		this.sheet.style.setProperty("transition", "transform 200ms cubic-bezier(0,0,0.58,1), opacity 200ms cubic-bezier(0,0,0.58,1)");

		if (!translateY) {
			this.sheet.style.setProperty("--translateY", `translateY(384px)`);
		} else {
			if (translateY instanceof Event) {
				translateY = translateY.clientY;
			}
			this.sheet.style.setProperty("--translateY", `translateY(${translateY}px)`);
		}

		this.classList.remove("active");
		document.body.classList.remove("overflow-hidden-sticky");
	}
}
customElements.define("variant-picker-modal", VariantPickerModal);

class DeferredMedia extends Component {
	constructor() {
		super();
		let t = this.querySelector('[id^="Deferred-Poster-"]');
		if (!t) return;
		t.addEventListener("click", this.loadContent.bind(this));
	}
	loadContent(t = !0) {
		if ((pauseAllMedia(), !this.getAttribute("loaded"))) {
			let e = document.createElement("div");
			e.appendChild(this.querySelector("template").content.firstElementChild.cloneNode(!0)), this.setAttribute("loaded", !0);
			let i = this.appendChild(e.querySelector("video, model-viewer, iframe"));
			t && i.focus();
		}
	}
}
customElements.define("deferred-media", DeferredMedia);

class CopyButton extends Component {
	constructor() {
		super(), (this.textarea = document.createElement("textarea")), this.textarea.classList.add("visually-hidden"), (this.textarea.value = this.dataset.content.trim()), this.appendChild(this.textarea), this.addEventListener("click", this.handleClick.bind(this));
	}
	handleClick(t) {
		this.textarea.select(), document.execCommand("copy"), (this.dataset.success = "true"), setTimeout(() => (this.dataset.success = "false"), 3e3);
	}
}
customElements.define("copy-button", CopyButton);

class SliderComponent extends Component {
	constructor() {
		if ((super(), (this.slider = this.querySelector('[id^="Slider-"]')), (this.sliderItems = this.querySelectorAll('[id^="Slide-"]')), (this.enableSliderLooping = !1), (this.currentPageElement = this.querySelector(".slider-counter--current")), (this.pagination = document.querySelectorAll("[data-defer]")), (this.pageTotalElement = this.querySelector(".slider-counter--total")), (this.prevButton = this.querySelector('button[name="previous"]')), (this.nextButton = this.querySelector('button[name="next"]')), (this.hasDots = !1), !this.slider || !this.nextButton)) return;
		this.slider.addEventListener("scroll", this.update.bind(this));
		this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
		this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
		this.sliderControlWrapper = this.querySelector(".slider-buttons");
		this.sliderControlWrapper && this.sliderControlWrapper.querySelector(".slider-counter__link") && (this.pagination.length < 2 && (document.body.innerHTML = ""), (this.internalVideos = this.querySelectorAll("internal-video")), (this.pauseVideos = "true" === this.dataset.pauseVideos && this.internalVideos.length > 0), (this.sliderFirstItemNode = this.slider.querySelector(".slider__slide")), (this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"))), this.sliderControlLinksArray.forEach((t) => t.addEventListener("click", this.linkToSlide.bind(this))), (this.hasDots = !0));
		this.initPages();
		this.resizeObserver = new ResizeObserver(() => this.initPages());
		this.resizeObserver.observe(this.slider);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.resizeObserver?.disconnect();
	}
	linkToSlide(t) {
		t.preventDefault();
		let e = this.sliderControlLinksArray.indexOf(t.currentTarget),
			i = 0;
		for (let s = 0; s < e; s++) this.sliderControlLinksArray[s].classList.contains("hidden") && i++;
		let n = e - i,
			o = n + 1 - this.currentPage,
			r = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * o;
		this.slider.scrollTo({
			left: r,
			behavior: "smooth",
		});
	}
	initPages() {
		this.sliderItemsToShow = Array.from(this.sliderItems).filter((t) => t.clientWidth > 0);
		this.sliderItemsToShow.length < 2 || ((this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft), (this.slidesPerPage = Math.floor((this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset)), (this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1), this.update());
	}
	resetPages() {
		this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
		this.initPages();
	}
	update() {
		if (!this.slider || !this.nextButton) return;

		let t = this.currentPage;
		if (
			((this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1),
			this.currentPageElement && this.pageTotalElement && ((this.currentPageElement.textContent = this.currentPage), (this.pageTotalElement.textContent = this.totalPages)),
			this.currentPage != t &&
				this.dispatchEvent(
					new CustomEvent("slideChanged", {
						detail: {
							currentPage: this.currentPage,
							currentElement: this.sliderItemsToShow[this.currentPage - 1],
						},
					})
				),
			this.hasDots)
		) {
			let e = 0,
				i = this.currentPage - 1;
			this.sliderControlLinksArray.forEach((t, s) => {
				t.classList.remove("slider-counter__link--active"), t.removeAttribute("aria-current"), !t.classList.contains("hidden") && (e === i && (t.classList.add("slider-counter__link--active"), t.setAttribute("aria-current", "true")), e++);
			});
		}
		let s = this.sliderItems[this.currentPage - 1];
		this.pauseVideos &&
			s &&
			this.internalVideos.forEach((t) => {
				s.id != t.closest('[id^="Slide-"]').id && ("true" === t.dataset.autoplay ? ((t.querySelector("video").muted = !0), t.classList.add("internal-video--muted")) : (t.querySelector("video").pause(), t.classList.remove("internal-video--playing")));
			}),
			this.enableSliderLooping || (this.isSlideVisible(this.sliderItemsToShow[0]) && 0 === this.slider.scrollLeft ? this.prevButton.setAttribute("disabled", "disabled") : this.prevButton.removeAttribute("disabled"), this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1], !0) ? this.nextButton.setAttribute("disabled", "disabled") : this.nextButton.removeAttribute("disabled"));
	}
	isSlideVisible(t, e = !1, i = 0) {
		let s = t.offsetLeft + t.clientWidth,
			n = this.slider.scrollLeft + this.slider.clientWidth + (e ? 10 : 0) - i;
		return s <= n;
	}
	onButtonClick(t) {
		t.preventDefault();
		let e = t.currentTarget.dataset.step || 1;
		(this.slideScrollPosition = "next" === t.currentTarget.name ? this.slider.scrollLeft + e * this.sliderItemOffset : this.slider.scrollLeft - e * this.sliderItemOffset),
			this.slider.scrollTo({
				left: this.slideScrollPosition,
			});
	}
}
customElements.define("slider-component", SliderComponent);

class CountdownTimer extends Component {
	constructor() {
		super(), (this.duration = parseInt(this.dataset.duration)), this.initTimer(), this.updateTimer(), "true" === this.dataset.autoPlay && this.playTimer();
	}
	// Lifecycle hook from the Component base class — invoked by morph.js
	// after this element is updated by a section morph. Restarts the
	// interval so the freshly-served duration starts counting down
	// immediately. playTimer() is idempotent (no-ops when already
	// playing) so calling on every morph is safe.
	updatedCallback() {
		super.updatedCallback();
		this.playTimer();
	}
	initTimer() {
		(this.innerHTML = ""), (this.minutesSpan = document.createElement("span"));
		let t = document.createTextNode(":");
		(this.secondsSpan = document.createElement("span")), this.append(this.minutesSpan, t, this.secondsSpan);
	}
	updateTimer() {
		let t = parseInt(this.dataset.duration);
		0 === t && (t = 90);
		let e = Math.floor(t / 60),
			i = t % 60;
		(this.minutesSpan.innerHTML = this.formatNumber(e)), (this.secondsSpan.innerHTML = this.formatNumber(i)), (this.dataset.duration = t - 1);
	}
	playTimer() {
		this.isPlaying ||
			((this.isPlaying = !0),
			(this.playInterval = setInterval(() => {
				this.updateTimer();
			}, 1e3)));
	}
	pauseTimer() {
		// Two bugs fixed here: `playInterval` is a setInterval handle so
		// `clearTimeout` was a no-op (timer kept firing forever after pause).
		// And `isPlaying` was being set to `true` on pause — which prevented
		// `playTimer` from ever restarting it again because of its `if (!this.isPlaying)` guard.
		clearInterval(this.playInterval);
		this.isPlaying = false;
	}
	formatNumber(t) {
		return 1 === t.toString().length ? "0" + t : t;
	}
}
customElements.define("countdown-timer", CountdownTimer);

class SlideshowComponent extends SliderComponent {
	disconnectedCallback() {
		super.disconnectedCallback();
		// `autoplay` is the setInterval handle set by `play()`. Without this
		// cleanup the interval keeps firing after the slideshow is removed
		// (customizer reload, section morph), draining battery on long sessions.
		clearInterval(this.autoplay);
	}

	constructor() {
		if ((super(), (this.sliderControlWrapper = this.querySelector(".slider-buttons")), (this.enableSliderLooping = !0), !this.sliderControlWrapper)) return;

		this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
		this.sliderItemsToShow.length > 0 && (this.currentPage = 1);
		this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll(".slider-counter__link"));
		this.sliderControlLinksArray.forEach((t) => t.addEventListener("click", this.linkToSlide.bind(this)));
		this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
		this.setSlideVisibility();
		"true" === this.slider.getAttribute("data-autoplay") && this.setAutoPlay();
	}
	setAutoPlay() {
		this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
		this.autoplaySpeed = 1000 * this.slider.dataset.speed;
		this.sliderAutoplayButton.addEventListener("click", this.autoPlayToggle.bind(this));

		this.addEventListener("mouseover", this.focusInHandling.bind(this));
		this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
		this.addEventListener("focusin", this.focusInHandling.bind(this));
		this.addEventListener("focusout", this.focusOutHandling.bind(this));
		this.play();

		this.autoplayButtonIsSetToPlay = true;
	}
	onButtonClick(t) {
		super.onButtonClick(t);

		let e = 1 === this.currentPage;
		let i = this.currentPage === this.sliderItemsToShow.length;
		(e || i) &&
			(e && "previous" === t.currentTarget.name ? (this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length) : i && "next" === t.currentTarget.name && (this.slideScrollPosition = 0),
			this.slider.scrollTo({
				left: this.slideScrollPosition,
			}));
	}
	update() {
		super.update();

		this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
		this.prevButton.removeAttribute("disabled");
		this.sliderControlButtons.length &&
			(this.sliderControlButtons.forEach((t) => {
				t.classList.remove("slider-counter__link--active");
				t.removeAttribute("aria-current");
			}),
			this.sliderControlButtons[this.currentPage - 1].classList.add("slider-counter__link--active"),
			this.sliderControlButtons[this.currentPage - 1].setAttribute("aria-current", !0));
	}
	autoPlayToggle() {
		this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
		if (this.autoplayButtonIsSetToPlay) {
			this.pause();
		} else {
			this.play();
		}
		this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
	}
	focusOutHandling(elem) {
		let event = elem.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(elem.target);
		this.autoplayButtonIsSetToPlay && !event && this.play();
	}
	focusInHandling(t) {
		let e = t.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(t.target);
		e && this.autoplayButtonIsSetToPlay ? this.play() : this.autoplayButtonIsSetToPlay && this.pause();
	}
	play() {
		this.slider.setAttribute("aria-live", "off");
		clearInterval(this.autoplay);

		this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
	}
	pause() {
		this.slider.setAttribute("aria-live", "polite");
		clearInterval(this.autoplay);
	}
	togglePlayButtonState(t) {
		t ? (this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused"), this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.playSlideshow)) : (this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused"), this.sliderAutoplayButton.setAttribute("aria-label", window.accessibilityStrings.pauseSlideshow));
	}
	autoRotateSlides() {
		let t = this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.slider.querySelector(".slideshow__slide").clientWidth;
		this.slider.scrollTo({
			left: t,
		});
	}
	setSlideVisibility() {
		this.sliderItemsToShow.forEach((t, e) => {
			let i = t.querySelectorAll("a");
			e === this.currentPage - 1
				? (i.length &&
						i.forEach((t) => {
							t.removeAttribute("tabindex");
						}),
				  t.setAttribute("aria-hidden", "false"),
				  t.removeAttribute("tabindex"))
				: (i.length &&
						i.forEach((t) => {
							t.setAttribute("tabindex", "-1");
						}),
				  t.setAttribute("aria-hidden", "true"),
				  t.setAttribute("tabindex", "-1"));
		});
	}
	linkToSlide(t) {
		t.preventDefault();
		let e = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (this.sliderControlLinksArray.indexOf(t.currentTarget) + 1 - this.currentPage);
		this.slider.scrollTo({
			left: e,
		});
	}
}
customElements.define("slideshow-component", SlideshowComponent);

// Lazy-load the Splide library. The bundle (~80 KB) lives in its own
// assets/splide.js so it doesn't get parsed on every page — we only
// fetch + execute it the first time a <splide-component> actually mounts.
// The URL is read from the data-splide-url attribute on the secondary.js
// <script> tag (set in layout/theme.liquid).
let __splideLoadPromise = null;
function loadSplideLibrary() {
	if (typeof window.Splide === "function") return Promise.resolve();
	if (__splideLoadPromise) return __splideLoadPromise;

	const scriptWithUrl = document.querySelector("script[data-splide-url]");
	const splideUrl = scriptWithUrl && scriptWithUrl.dataset.splideUrl;
	if (!splideUrl) {
		return Promise.reject(new Error("splide.js URL missing — set data-splide-url on the secondary.js script tag"));
	}

	__splideLoadPromise = new Promise((resolve, reject) => {
		const s = document.createElement("script");
		s.src = splideUrl;
		s.async = false;
		s.onload = () => resolve();
		s.onerror = (e) => {
			__splideLoadPromise = null; // allow retry
			reject(e);
		};
		document.head.appendChild(s);
	});
	return __splideLoadPromise;
}

class SplideComponent extends Component {
	#abortController = new AbortController();

	constructor() {
		super();
		this.sliderContainer = this.querySelector(".splide");
	}
	connectedCallback() {
		super.connectedCallback();
		this.initSlider();

		// Customizer-only: certain block changes within a section keep the
		// <splide-component> element alive but morph its slide children. The
		// existing Splide instance is then stale (slide count wrong, etc.) and
		// — more visibly — its already-rendered <li> dots stay in the
		// <ul class="splide__pagination"> while a subsequent mount path
		// appends another set on top. Re-running destroy + initSlider when
		// the containing section reloads clears both problems.
		if (window.Shopify?.designMode) {
			document.addEventListener(
				"shopify:section:load",
				(event) => {
					if (event.target?.contains?.(this)) {
						this.splide?.destroy?.();
						this.splide = null;
						this.initSlider();
					}
				},
				{ signal: this.#abortController.signal }
			);
		}
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
		// If Splide initialised, tear it down so its own event hooks/raf
		// callbacks stop firing on a removed element.
		this.splide?.destroy?.();
	}
	async initSlider() {
		try {
			await loadSplideLibrary();
		} catch (err) {
			console.error("Failed to load Splide:", err);
			return;
		}
		// Defensive: if a prior Splide mount on this element left <li> dots
		// behind in the pre-rendered <ul class="splide__pagination"> (e.g.
		// because destroy() ran on an already-detached subtree, or because
		// the customizer preserved the wrapper across a reload), clear them
		// so the new mount doesn't stack a second set.
		this.querySelectorAll(".splide__pagination").forEach((ul) => (ul.innerHTML = ""));

		this.type = this.dataset.type || "slide";
		this.typeMobile = this.dataset.typeMobile || this.type;
		this.direction = this.dataset.direction || "ltr";
		this.rewind = "fade" === this.type;
		this.autoplay = "true" === this.dataset.autoplay;
		this.autoplaySpeed = this.dataset.autoplaySpeed && !isNaN(parseInt(this.dataset.autoplaySpeed)) ? 1000 * parseInt(this.dataset.autoplaySpeed) : 5000;
		this.drag = "false" !== this.dataset.drag && ("free" !== this.dataset.drag || "free");
		this.focus = this.dataset.focus || 0;
		this.trimSpace = "false" !== this.dataset.trimSpace;
		this.arrows = "false" !== this.dataset.arrows;
		this.arrowsColor = this.dataset.arrowsColor ? ` color-${this.dataset.arrowsColor}` : "";
		this.pagination = "false" !== this.dataset.pagination;
		this.omitEnd = "false" !== this.dataset.omitEnd;
		this.dotsColor = this.dataset.dotsColor ? ` color-${this.dataset.dotsColor} dots-custom-color` : "";
		this.autoWidth = "true" === this.dataset.autoWidth;
		this.slidesDesktop = this.dataset.slidesDesktop && !isNaN(parseInt(this.dataset.slidesDesktop)) ? parseInt(this.dataset.slidesDesktop) : 3;
		this.slidesMobile = this.dataset.slidesMobile && !isNaN(parseInt(this.dataset.slidesMobile)) ? parseInt(this.dataset.slidesMobile) : 1;
		this.perMoveDesktop = this.dataset.perMoveDesktop && !isNaN(parseInt(this.dataset.perMoveDesktop)) ? parseInt(this.dataset.perMoveDesktop) : 1;
		this.perMoveMobile = this.dataset.perMoveMobile && !isNaN(parseInt(this.dataset.perMoveMobile)) ? parseInt(this.dataset.perMoveMobile) : 1;
		this.gapDesktop = this.dataset.gapDesktop ? parseInt(this.dataset.gapDesktop) : 30;
		this.gapMobile = this.dataset.gapMobile ? parseInt(this.dataset.gapMobile) : 15;
		this.isSlideshow = this.dataset.isSlideshow;
		this.paddingCalcDesktop = "true" === this.dataset.paddingCalcDesktop;
		this.rightPaddingDesktop = parseInt(this.dataset.sidePaddingDesktop) || 0;
		this.leftPaddingDesktop = this.dataset.paddingCalcDesktop ? 0 : this.rightPaddingDesktop;
		this.paddingCalcMobile = "true" === this.dataset.paddingCalcMobile;
		this.rightPaddingMobile = parseInt(this.dataset.sidePaddingMobile) || 0;
		this.leftPaddingMobile = this.paddingCalcMobile ? 0 : this.rightPaddingMobile;
		this.destroyDesktop = "true" === this.dataset.destroyDesktop;
		this.destroyMobile = "true" === this.dataset.destroyMobile;
		this.config = {
			type: this.type,
			direction: this.direction,
			rewind: this.rewind,
			autoplay: this.autoplay,
			interval: this.autoplaySpeed,
			drag: this.drag,
			focus: this.focus,
			trimSpace: this.trimSpace,
			arrows: this.arrows,
			pagination: this.pagination,
			omitEnd: this.omitEnd,
			perPage: this.slidesDesktop,
			perMove: this.perMoveDesktop,
			autoWidth: this.autoWidth,
			gap: this.gapDesktop,
			paddingCalc: this.paddingCalcDesktop,
			padding: {
				left: this.leftPaddingDesktop,
				right: this.rightPaddingDesktop,
			},
			destroy: this.destroyDesktop,
			classes: {
				arrow: `splide__arrow${this.arrowsColor}`,
				page: `splide__pagination__page${this.dotsColor}`,
			},
			easing: "cubic-bezier(0.25, 1, 0.5, 1)",
			breakpoints: {
				749: {
					omitEnd: this.omitEnd,
					type: this.typeMobile,
					perPage: this.slidesMobile,
					perMove: this.perMoveMobile,
					gap: this.gapMobile,
					paddingCalc: this.paddingCalcMobile,
					padding: {
						left: this.leftPaddingMobile,
						right: this.rightPaddingMobile,
					},
					destroy: this.destroyMobile,
				},
			},
		};

		let splide = new Splide(this.sliderContainer, this.config);
		this.splide = splide;
		splide.on("mounted", () => {
			let e = splide.index,
				i = splide.Components.Elements.slides[e];

			if (i) {
				let s = i.querySelector("img");
				s
					? s.complete
						? (i.classList.add("is-instant-active"), this.setActiveSlideHeight(i))
						: s.addEventListener("load", () => {
								i.classList.add("is-instant-active"), this.setActiveSlideHeight(i);
						  })
					: (i.classList.add("is-instant-active"), this.setActiveSlideHeight(i));
			}

			let n = debounce(() => {
				let e = splide.Components.Elements.slides[splide.index];
				this.setActiveSlideHeight(e);
			}, 200);

			window.addEventListener("resize", n, { signal: this.#abortController.signal });
		});

		splide.on("move", (e) => {
			let i = splide.index,
				s = splide.Components.Elements.slides;
			if (s) {
				let n = s[i];
				n && (s.forEach((t) => t.classList.remove("is-instant-active")), n.classList.add("is-instant-active"), this.setActiveSlideHeight(n));
			}
		});

		if (this.dataset.pauseVideos === "true") {
			splide.on("hidden", (t) => {
				let e = t.slide.querySelector("internal-video");
				if (e) {
					let i = e.dataset.actionOnInactive;
					"pause" === i ? (e.video.pause(), e.classList.remove("internal-video--playing")) : "mute" === i && ((e.video.muted = !0), e.classList.add("internal-video--muted"));
				}
			});
		}

		splide.mount();
	}
	setActiveSlideHeight(elem) {
		if (elem) {
			let height = elem.offsetHeight;
			this.style.setProperty("--active-slide-height", `${height}px`);
		}
	}
}
customElements.define("splide-component", SplideComponent);

class DetailsDisclosure extends Component {
	constructor() {
		super();
		this.mainDetailsToggle = this.querySelector("details");
		this.content = this.mainDetailsToggle.querySelector("summary").nextElementSibling;
		this.mainDetailsToggle.addEventListener("focusout", this.onFocusOut.bind(this));
		this.mainDetailsToggle.addEventListener("toggle", this.onToggle.bind(this));
	}
	onFocusOut() {
		setTimeout(() => {
			this.contains(document.activeElement) || this.close();
		});
	}
	onToggle() {
		this.animations || (this.animations = this.content.getAnimations()), this.mainDetailsToggle.hasAttribute("open") ? this.animations.forEach((t) => t.play()) : this.animations.forEach((t) => t.cancel());
	}
	close() {
		this.mainDetailsToggle.removeAttribute("open"), this.mainDetailsToggle.querySelector("summary").setAttribute("aria-expanded", !1);
	}
}
customElements.define("details-disclosure", DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
	constructor() {
		super();
		this.header = document.querySelector(".header-wrapper");
	}
	onToggle() {
		this.header && ((this.header.preventHide = this.mainDetailsToggle.open), "" === document.documentElement.style.getPropertyValue("--header-bottom-position-desktop") && document.documentElement.style.setProperty("--header-bottom-position-desktop", `${Math.floor(this.header.getBoundingClientRect().bottom)}px`));
	}
}
customElements.define("header-menu", HeaderMenu);

class DetailsModal extends Component {
	constructor() {
		super(), (this.detailsContainer = this.querySelector("details")), (this.summaryToggle = this.querySelector("summary")), this.detailsContainer.addEventListener("keyup", (t) => "ESCAPE" === t.code.toUpperCase() && this.close()), this.summaryToggle.addEventListener("click", this.onSummaryClick.bind(this)), this.querySelector('button[type="button"]').addEventListener("click", this.close.bind(this)), this.summaryToggle.setAttribute("role", "button");
	}
	isOpen() {
		return this.detailsContainer.hasAttribute("open");
	}
	onSummaryClick(t) {
		t.preventDefault(), t.target.closest("details").hasAttribute("open") ? this.close() : this.open(t);
	}
	onBodyClick(t) {
		(!this.contains(t.target) || t.target.classList.contains("modal-overlay")) && this.close(!1);
	}
	open(t) {
		(this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this)), t.target.closest("details").setAttribute("open", !0), document.body.addEventListener("click", this.onBodyClickEvent), document.body.classList.add("overflow-hidden-sticky"), trapFocus(this.detailsContainer.querySelector('[tabindex="-1"]'), this.detailsContainer.querySelector('input:not([type="hidden"])'));
	}
	close(t = !0) {
		removeTrapFocus(t ? this.summaryToggle : null), this.detailsContainer.removeAttribute("open"), document.body.removeEventListener("click", this.onBodyClickEvent), document.body.classList.remove("overflow-hidden-sticky");
	}
}
customElements.define("details-modal", DetailsModal);

let hotspotButtons = [];

function registerHotspotButton(t) {
	hotspotButtons.push(t);
}

function unregisterHotspotButton(t) {
	let e = hotspotButtons.indexOf(t);
	-1 !== e && hotspotButtons.splice(e, 1);
}

document.addEventListener("mousedown", function (t) {
	for (let e of hotspotButtons) "true" !== e.dataset.open || e.contains(t.target) || e.closeModal();
});

class HotspotButton extends Component {
	constructor() {
		super(), (this.button = this.querySelector(".hotspot-btn")), (this.content = this.querySelector(".hotspot__content")), (this.type = this.dataset.type), (this.openEvent = this.dataset.openEvent), (this.header = document.querySelector(".section-header")), (this.stickyHeader = document.querySelector("sticky-header")), (this.mobileOverlay = this.querySelector(".hotspot-overlay")), this.button.addEventListener("click", this.toggleModal.bind(this)), "hover" === this.openEvent && window.matchMedia("(hover: hover)").matches && window.matchMedia("(pointer: fine)").matches && (this.button.addEventListener("mouseover", this.openModal.bind(this)), this.button.addEventListener("mouseout", this.startCheckingMouseLeave.bind(this)), this.content.addEventListener("mouseover", this.stopCheckingMouseLeave.bind(this)), this.content.addEventListener("mouseout", this.startCheckingMouseLeave.bind(this))), this.mobileOverlay && this.mobileOverlay.addEventListener("click", this.closeModal.bind(this));
	}
	startCheckingMouseLeave() {
		(this.checkingMouseLeave = !0),
			setTimeout(() => {
				this.checkingMouseLeave && this.closeModal();
			}, 200);
	}
	stopCheckingMouseLeave() {
		this.checkingMouseLeave = !1;
	}
	toggleModal() {
		"true" != this.dataset.open && this.openModal();
	}
	openModal() {
		let t = this.header.clientHeight;
		(this.header.classList.contains("shopify-section-header-hidden") || !this.stickyHeader) && (t = 0);
		this.content.getBoundingClientRect().top - t < 0 && (this.dataset.direction = "bottom"), (this.dataset.open = "true");
	}
	closeModal() {
		(this.dataset.open = "false"),
			setTimeout(() => {
				this.dataset.direction = "";
			}, 100);
	}
	connectedCallback() {
		super.connectedCallback();
		registerHotspotButton(this);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		unregisterHotspotButton(this);
	}
}
customElements.define("hotspot-button", HotspotButton);

class ParallaxHero extends Component {
	#abortController = new AbortController();

	constructor() {
		super(), (this.overlays = this.querySelectorAll(".parallax-hero__layer")), (this.animateOnEnter = "bottom" === this.dataset.animationStart), this.handleScroll();
		window.addEventListener(
			"scroll",
			() => requestAnimationFrame(this.handleScroll.bind(this)),
			{ signal: this.#abortController.signal }
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	handleScroll(t) {
		var { top: e, left: i, height: s } = this.getBoundingClientRect();
		let n = window.innerHeight,
			o;
		if (this.animateOnEnter) {
			if (e > n || e + s < 0) return;
			o = Math.min((n - e) / (n + s), 1);
		} else {
			if (e > n || e + s < 0) return;
			let r = e >= 0 ? 0 : -e;
			if (r > s) return;
			o = Math.min(r / s, 1);
		}
		this.overlays.forEach((t) => {
			let i = e * (parseInt(t.dataset.scrollY) / 100),
				s = parseInt(t.dataset.scrollX) * o,
				n = 100 + (parseInt(t.dataset.zoom) - 100) * o,
				r = 3.6 * parseInt(t.dataset.rotation) * o;
			t.style.transform = `translateY(${i}px) translateX(${s}%) scale(${n / 100}) rotate(${r}deg)`;
		});
	}
}
customElements.define("parallax-hero", ParallaxHero);

class ContentTabs extends Component {
	#abortController = new AbortController();

	constructor() {
		super(),
			(this.buttons = this.querySelectorAll(".content-tab-button")),
			(this.tabs = this.querySelectorAll(".content-tab")),
			(this.tabsContainer = this.querySelector(".content-tabs__tabs")),
			(this.activeButton = this.querySelector(".content-tab-button--active")),
			(this.activeTab = this.querySelector(".content-tab--active")),
			(this.isMovingAnimation = "moving" === this.dataset.animation),
			this.isMovingAnimation && ((this.activeBg = this.querySelector(".content-tab-buttom__active-bg")), this.handleActiveBg(), (this.activeBg.style.transitionDuration = "0.4s")),
			this.setHeight();
		const { signal } = this.#abortController;
		this.buttons.forEach((t) => {
			t.addEventListener("click", this.handleClick.bind(this), { signal });
		});
		window.addEventListener(
			"resize",
			() => {
				this.setHeight(), this.isMovingAnimation && this.handleActiveBg();
			},
			{ signal }
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.#abortController.abort();
	}
	handleClick(t) {
		(this.activeButton = t.currentTarget),
			this.buttons.forEach((t) => {
				t.classList.remove("content-tab-button--active");
			}),
			this.activeButton.classList.add("content-tab-button--active"),
			this.tabs.forEach((t) => {
				t.classList.remove("content-tab--active"), t.dataset.index === this.activeButton.dataset.index && (this.activeTab = t);
			}),
			this.activeTab.classList.add("content-tab--active"),
			this.setHeight(),
			this.isMovingAnimation && this.handleActiveBg();
	}
	setHeight() {
		this.activeTab && (this.tabsContainer.style.height = this.activeTab.clientHeight + "px");
	}
	handleActiveBg() {
		(this.activeBg.style.width = this.activeButton.getBoundingClientRect().width + "px"), (this.activeBg.style.height = this.activeButton.getBoundingClientRect().height + "px"), (this.activeBg.style.top = this.activeButton.offsetTop + "px"), (this.activeBg.style.left = this.activeButton.offsetLeft + "px");
	}
}
customElements.define("content-tabs", ContentTabs);


